import React, { useState, useEffect } from "react";
import axios from "axios";
import '../assets/CSS/messages.css';
import '../assets/CSS/conversations.css';
import profilePicPlaceholder from '../assets/Images/ProfilePic-placeholder.jpg';

const Messages = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loggedInUser = JSON.parse(localStorage.getItem('loggedInUser'));

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/conversations`, {
          params: { userId: loggedInUser?.id }
        });
        setConversations(response.data);
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch conversations");
        setLoading(false);
      }
    };
    fetchConversations();
  }, [loggedInUser?.id]);

  useEffect(() => {
    if (!selectedConversationId) return;

    const fetchMessages = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/messages/conversation/${selectedConversationId}`
        );
        setMessages(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Error fetching messages", err);
      }
    };
    fetchMessages();
  }, [selectedConversationId]);

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!searchTerm.trim()) {
        setSearchResults([]);
        return;
      }

      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/search`, {
          params: { query: searchTerm }
        });

        const existingUserIds = conversations.map(c => c.otherUserId);
        const filtered = response.data.filter(user =>
          user.id !== loggedInUser.id &&
          !existingUserIds.includes(user.id)
        );

        setSearchResults(filtered);
      } catch (err) {
        console.error("Failed to search users", err);
      }
    };

    fetchSearchResults();
  }, [searchTerm, conversations, loggedInUser.id]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/messages`, {
        conversationId: selectedConversationId,
        content: newMessage,
        senderUserId: loggedInUser?.id,
        receiverUserId: selectedConversation.otherUserId
      });

      setNewMessage("");

      const conversationsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/conversations`, {
        params: { userId: loggedInUser?.id }
      });
      setConversations(conversationsResponse.data);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/messages/conversation/${selectedConversationId}`
      );
      setMessages(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
    if (err.response && err.response.data) {
      setError(err.response.data); // ← viser feilmelding fra backend
    } else {
      setError("Noe gikk galt under sending.");
    }
  }
  };

  const filteredConversations = conversations.filter(conv =>
    (conv.otherUserName ?? '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
  return <div>Loading...</div>;
}

  return (
    <div id="messages">
      {!selectedConversationId ? (
        <div className="conversation-list">
          <h2>Conversations</h2>

          <input
            type="text"
            placeholder="Find users or conversations..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          {filteredConversations.length === 0 && searchResults.length === 0 ? (
            <p>No conversations started yet!</p>
          ) : (
            <>
              {filteredConversations.map((conv, index) => (
                <div
                  key={`conv-${index}`}
                  className={`conversation-item ${conv.conversationId === selectedConversationId ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedConversationId(conv.conversationId);
                    setSelectedConversation(conv);
                  }}
                >
                  <h3>{conv.otherUserName}</h3>
                  <p>{conv.latestMessage ? conv.latestMessage : "Zing!"}</p> 
                </div>
              ))}

              {searchResults.length > 0 && (
                <>
                  <h3>Start a new conversation</h3>
                  {searchResults.map((user, index) => (
                    <div
                      key={`search-${index}`}
                      className="conversation-item"
                      onClick={async () => {
                        try {
                          const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/conversations`, {
                            userId1: loggedInUser.id,
                            userId2: user.id
                          });

                          const newConv = response.data;

                          newConv.otherUserName = `${user.firstName} ${user.lastName}`;
                          
                          setConversations(prev => [...prev, newConv]);
                          setSelectedConversationId(newConv.conversationId);
                          setSelectedConversation(newConv);
                          setMessages([]); 
                          setMessages([]);
                          setSearchTerm("");
                          setSearchResults([]);
                        } catch (err) {
                          console.error("Failed to start conversation", err);
                        }
                      }}
                    >
                      <h3>{user.firstName} {user.lastName}</h3>
                      <p>Press to start conversation</p>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="conversation-messages">
          <button
            className="back-button"
            onClick={() => {
              setSelectedConversationId(null);
              setSelectedConversation(null);
              setMessages([]);
            }}
          >
            ← Back
          </button>

          <h2>{selectedConversation?.otherUserName}</h2>

          <div className="message-scroll">
            {Array.isArray(messages) && messages.map((msg, index) => (
              <div key={index} className={`message-item ${msg.senderUserId === loggedInUser.id ? 'own-message' : ''}`}>
                <div className="avatar-circle">
                  <img
                    src={
                      msg.senderProfilePicUrl
                        ? `${process.env.REACT_APP_API_URL}${msg.senderProfilePicUrl}`
                        : profilePicPlaceholder
                    }
                    alt={`${msg.sender?.firstName ?? "User"} ${msg.sender?.lastName ?? ""}`}
                    className="avatar-image"
                  />
                </div>

                <div className="message-content">
                  <h3>
                    {msg.sender
                      ? `${msg.sender.firstName} ${msg.sender.lastName}`
                      : "Unknown"}
                  </h3>
                  <p>{msg.content}</p>
                  <small>
                    {new Date(msg.timestamp).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </small>
                </div>
              </div>
            ))}
          </div>

          <div className="message-input-container">
            <input
              type="text"
              placeholder="Write something..."
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                if (error) setError(null); // fjern error når brukeren skriver på nytt
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSendMessage();
              }}
            />
            <button onClick={handleSendMessage}>Send</button>

            {error && (
              <div className="error-message" style={{ color: 'red', marginTop: '4px' }}>
                {error}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;
